import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoComplete from "components/AutoComplete";
import { ProductsApi } from "apis/ProductsApi";
import { showToast } from "utils";

/*
 * Reusable TargetGroup sub-form — used by both the scheme form's
 * "TargetGroups" repeater and the standalone Template form (§4 of the
 * handoff doc). A template is just this same shape saved on its own.
 */
export default function TargetGroupBuilder({
  targetGroup,
  onChange,
  onRemove,
  brandId,
  templates,
  showRemove,
  sessionKey,
}) {
  const set = (field, value) => onChange({ ...targetGroup, [field]: value });
  const setFilter = (field, value) =>
    onChange({
      ...targetGroup,
      attributeFilters: { ...targetGroup.attributeFilters, [field]: value },
    });

  const fetchProducts = async (keyword) => {
    if (!brandId) {
      showToast("Select a brand first");
      return [];
    }
    try {
      const { data } = await ProductsApi.SearchProducts({
        page: 0,
        size: 20,
        keyword,
        brandId,
        isActive: true,
      });
      return data.data || [];
    } catch (err) {
      showToast(err?.response?.data?.message || "Error fetching products");
      return [];
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    set("templateId", templateId);
    // Template copy-embed happens server-side on save; here we just keep
    // the reference — any field the user edits afterwards still wins.
  };

  const handleProductsChange = (_, value) => {
    onChange({
      ...targetGroup,
      productIds: value.map((p) => p.id),
      products: value,
    });
  };

  return (
    <Box sx={{ p: 2, border: "1px solid #E2E8F0", borderRadius: 1.5, mb: 2, background: "#F8FAFC" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Target Group</Typography>
        {showRemove && (
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        {templates && templates.length > 0 && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Use template</InputLabel>
              <Select label="Use template" value={targetGroup.templateId || ""} onChange={handleTemplateChange}>
                <MenuItem value="">None — build manually</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name || t.targetGroup?.name || t.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {targetGroup.templateId && (
              <Typography variant="caption" color="text.secondary">
                Products/filters will be copied from the template on save — not linked. Editing fields below overrides the template's values.
              </Typography>
            )}
          </Grid>
        )}

        <Grid item xs={12} sm={templates && templates.length > 0 ? 6 : 12}>
          <TextField
            fullWidth
            size="small"
            label="Target Group Name"
            value={targetGroup.name || ""}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. A37 & A57 Upgrade Variants"
          />
        </Grid>

        <Grid item xs={12}>
          <AutoComplete
            label="Products"
            sessionKey={sessionKey}
            multiple
            value={targetGroup.products || []}
            getOptionLabel={(option) => `${option.primaryName || ""} ${option.secondaryName || ""}`.trim()}
            fetchOptions={fetchProducts}
            onChange={handleProductsChange}
          />
          <Typography variant="caption" color="text.secondary">
            Leave empty and use color/variant/DP filters below only if that's intentional — an empty target group with no filters selects the entire brand catalog and will fail publish.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Color IDs (comma-separated, optional)"
            value={targetGroup.attributeFilters?.colorIds || ""}
            onChange={(e) => setFilter("colorIds", e.target.value)}
            placeholder="colorId1, colorId2"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Variant Filters (optional JSON)"
            value={targetGroup.attributeFilters?.variantFilters || ""}
            onChange={(e) => setFilter("variantFilters", e.target.value)}
            placeholder='{"RAM":["8GB","12GB"]}'
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="DP Band ≥"
            value={targetGroup.attributeFilters?.dpBandGte || ""}
            onChange={(e) => setFilter("dpBandGte", e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="DP Band ≤"
            value={targetGroup.attributeFilters?.dpBandLte || ""}
            onChange={(e) => setFilter("dpBandLte", e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

/* Builds the wire-format attributeFilters object from the UI's string fields,
 * omitting anything unused (per §3 "all optional; omit what's unused"). */
export function buildAttributeFilters(attributeFilters) {
  if (!attributeFilters) return undefined;
  const result = {};

  const colorIds = (attributeFilters.colorIds || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (colorIds.length) result.colorIds = colorIds;

  if (attributeFilters.variantFilters && attributeFilters.variantFilters.trim()) {
    try {
      result.variantFilters = JSON.parse(attributeFilters.variantFilters);
    } catch (err) {
      throw new Error("Variant Filters must be valid JSON, e.g. {\"RAM\":[\"8GB\",\"12GB\"]}");
    }
  }

  const gte = attributeFilters.dpBandGte;
  const lte = attributeFilters.dpBandLte;
  if (gte !== "" && gte !== undefined || lte !== "" && lte !== undefined) {
    result.dpBand = {
      ...(gte !== "" && gte !== undefined ? { gte: Number(gte) } : {}),
      ...(lte !== "" && lte !== undefined ? { lte: Number(lte) } : {}),
    };
  }

  return Object.keys(result).length ? result : undefined;
}

/* Reverse mapping — server response → UI string fields, for edit/amend. */
export function mapAttributeFiltersToUi(attributeFilters) {
  if (!attributeFilters) {
    return { colorIds: "", variantFilters: "", dpBandGte: "", dpBandLte: "" };
  }
  return {
    colorIds: (attributeFilters.colorIds || []).join(", "),
    variantFilters: attributeFilters.variantFilters
      ? JSON.stringify(attributeFilters.variantFilters)
      : "",
    dpBandGte: attributeFilters.dpBand?.gte ?? "",
    dpBandLte: attributeFilters.dpBand?.lte ?? "",
  };
}
